/**
 * Email Verification Page
 *
 * Allows users to verify their email after registration using a code sent by Cognito.
 */
import { useLocation, useSearchParams } from 'react-router-dom';

import { useEffect, useState } from 'react';

import { useFormWithValidation } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  Alert,
  Card,
  FormButton,
  FormField,
  H1,
  H2,
  Text,
} from '@splitfiesta/ui';

import { AuthPageSkeleton } from '@/components/common/AuthPageSkeleton';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { Footer } from '@/components/layout/Footer';

import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const email = searchParams.get('email') || '';

  // Get password from navigation state (passed from register page for auto-login)
  // Store in state and immediately clear from history for security
  const [password] = useState(() => {
    const pwd = (location.state as { password?: string })?.password;
    // Clear sensitive data from history state immediately
    if (pwd) {
      window.history.replaceState({}, document.title);
    }
    return pwd;
  });

  const [code, setCode] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);

  const { t, ready } = useTranslation();
  const { handleError } = useErrorHandler('auth');
  const {
    confirmSignUpAsync,
    isConfirmingSignUp,
    confirmSignUpError,
    resendCode,
    isResendingCode,
    resendCodeError,
  } = useAuth();

  // Load required namespaces
  const authLoaded = useNamespace('auth');
  const commonLoaded = useNamespace('common');
  const errorsLoaded = useNamespace('errors');

  // Form validation and submission
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting,
    error,
    setError,
  } = useFormWithValidation({
    onSubmit: async () => {
      // Pass password for auto-login after verification (if available from registration)
      await confirmSignUpAsync({ email, code, password });
    },
    validateFn: () => {
      if (!code.trim()) {
        return t('auth:errors.codeRequired');
      }
      if (code.length !== 6) {
        return t('auth:errors.invalidCode');
      }
      return null;
    },
  });

  // Handle confirmation errors
  useEffect(() => {
    if (confirmSignUpError) {
      setError(handleError(confirmSignUpError));
    }
  }, [confirmSignUpError, handleError, setError]);

  // Handle resend errors
  useEffect(() => {
    if (resendCodeError) {
      setError(handleError(resendCodeError));
    }
  }, [resendCodeError, handleError, setError]);

  const handleResendCode = () => {
    setResendSuccess(false);
    resendCode(email);
    // Show success message after a short delay (assuming success)
    setTimeout(() => setResendSuccess(true), 1000);
  };

  const isLoading = isSubmitting || isConfirmingSignUp;

  // Show skeleton while translations load
  if (!ready || !authLoaded || !commonLoaded || !errorsLoaded) {
    return <AuthPageSkeleton fieldCount={1} />;
  }

  // Redirect if no email
  if (!email) {
    return (
      <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
        <div className="flex-1 p-4 flex items-center justify-center">
          <Card>
            <Alert
              type="error"
              message={t('auth:errors.invalidEmail')}
              className="mb-4"
            />
            <RouteChangeLink
              href="/login"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
            >
              {t('auth:verifyEmail.backToLogin')}
            </RouteChangeLink>
          </Card>
        </div>
        <Footer variant="minimal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
      <div className="flex-1 p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="text-center mb-8 mt-8">
            <RouteChangeLink href="/">
              <H1 className="text-white hover:text-white/90 transition-colors cursor-pointer drop-shadow-lg mb-2">
                {t('common:app.name')}
              </H1>
            </RouteChangeLink>
          </div>

          <Card>
            <div className="text-center mb-6">
              <H2>{t('auth:verifyEmail.title')}</H2>
              <Text variant="bodySmall" className="mt-1">
                {t('auth:verifyEmail.subtitle', { email })}
              </Text>
            </div>

            {error && (
              <Alert
                type="error"
                message={String(error)}
                className="mb-6"
                data-testid="verify-error-message"
              />
            )}

            {resendSuccess && (
              <Alert
                type="success"
                message={t('auth:verifyEmail.resendSuccess')}
                className="mb-6"
                data-testid="resend-success-message"
              />
            )}

            <form
              onSubmit={e => handleFormSubmit(e, { code })}
              className="space-y-4"
              data-testid="verify-form"
            >
              <FormField
                id="code"
                type="text"
                label={t('auth:verifyEmail.codePlaceholder')}
                value={code}
                onChange={setCode}
                placeholder="123456"
                required
                autoFocus
                disabled={isLoading}
                maxLength={6}
                autoComplete="one-time-code"
                data-testid="verify-code-input"
              />

              <FormButton
                type="submit"
                loading={isLoading}
                loadingText={t('common:actions.loading')}
                fullWidth
                data-testid="verify-submit-button"
              >
                {t('auth:verifyEmail.submit')}
              </FormButton>
            </form>

            <div className="mt-6 text-center">
              <Text variant="bodySmall">
                {t('auth:verifyEmail.resendCode')}{' '}
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResendingCode}
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium disabled:opacity-50"
                  data-testid="resend-code-button"
                >
                  {t('auth:verifyEmail.resendLink')}
                </button>
              </Text>
            </div>

            <div className="mt-4 text-center">
              <RouteChangeLink
                href="/login"
                className="text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-sm"
                data-testid="back-to-login-link"
              >
                {t('auth:verifyEmail.backToLogin')}
              </RouteChangeLink>
            </div>
          </Card>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}
